// NOTE: This is a variation of the modal in "SavePaymentMethod.tsx" that will be used
// in Product Platform's new "Credit Card Failure" project. This will only be called behind
// a feature flag for the uses of the Credit Card Failure project.
import { Alert, Box, Button, Typography } from "@outschool/backpack";
import { centsToDollars } from "@outschool/business-rules";
import { ActiveOngoingEnrollmentsPausedDueToPaymentFailureQuery } from "@outschool/gql-frontend-generated";
import { useLocale, useTranslation } from "@outschool/localization";
import { enrolledPath } from "@outschool/routes";
import { gql, useMutation, useQuery } from "@outschool/ui-apollo";
import { Modal } from "@outschool/ui-legacy-component-library";
import { pxToRem } from "@outschool/ui-utils";
import React from "react";

import {
  PauseOngoingEnrollmentsMutation,
  PauseOngoingEnrollmentsMutationVariables
} from "../../../shared/generated/graphql";
import {
  useCreateSetupIntent,
  usePaymentMethods
} from "../../hooks/CurrentUser";
import { pauseOngoingEnrollmentsMutation } from "../../queries/EnrollmentMutations";
import { PriceDisplay } from "../enrolling/enrollmentForm/priceBreakdown/PriceDisplay";
import ErrorMessage from "../ErrorMessage";
import Link from "../Link";
import LoadingWithMessage from "../LoadingWithMessage";
import ResumeSubscriptionsAuthorization from "../payments/ResumeSubscriptionsAuthorization";
import {
  BillingDetails,
  StripeProvider,
  useDefaultStripeErrorMessage,
  useGetCard
} from "../payments/Stripe";
import StripeNewCard from "../payments/StripeNewCard";
import StripeSavedCards from "../payments/StripeSavedCards";

export const activeOngoingEnrollmentsPausedDueToPaymentFailureQuery = gql`
  query ActiveOngoingEnrollmentsPausedDueToPaymentFailure {
    currentUser {
      uid
      activeOngoingEnrollmentsPausedDueToPaymentFailure {
        uid
        priceCents
        ongoingEndTime
        section_uid
        section {
          uid
          activity {
            uid
            title
          }
        }
        learner {
          uid
          name
          age
        }
      }
    }
  }
`;

export function useEnrollmentsPausedDueToPaymentFailure() {
  return useQuery<ActiveOngoingEnrollmentsPausedDueToPaymentFailureQuery>(
    activeOngoingEnrollmentsPausedDueToPaymentFailureQuery
  );
}

type PaymentFailureEnrollment = {
  uid: string;
  priceCents: number | null;
  ongoingEndTime: any;
  section_uid: string | null;
  section: {
    uid: string;
    activity: {
      uid: string;
      title: string;
    };
  } | null;
  learner: {
    uid: string;
    name: string | null;
    age: number | null;
  } | null;
};

function UnsubscribedClassesList({
  unsubscribedEnrollments
}: {
  unsubscribedEnrollments: PaymentFailureEnrollment[];
}) {
  const { t } = useTranslation(
    "client\\components\\account\\UpdatePaymentMethodDueToPaymentFailure"
  );
  const locale = useLocale();
  const message = t`You were unsubscribed to the following classes. We recommend checking the class page to find available sections or messaging your teachers:`;

  return (
    <Box
      sx={{
        backgroundColor: "warning.200",
        borderRadius: pxToRem(8),
        padding: pxToRem(16)
      }}
    >
      <Typography sx={{ bottomPadding: pxToRem(10) }}>{message}</Typography>
      <ul
        style={{
          listStyleType: "disc",
          listStylePosition: "outside",
          marginLeft: pxToRem(16)
        }}
      >
        {unsubscribedEnrollments.map((unsubscribedEnrollment, index) => (
          <li key={index}>
            <Box>
              {/* TODO: Fix this link, it always sends to enrolled/undefined/ and I'm not sure why */}
              <Link
                to={enrolledPath(
                  unsubscribedEnrollment?.section?.uid ?? "",
                  locale
                )}
              >
                {unsubscribedEnrollment?.section?.activity?.title ?? `------`}
              </Link>
              <Box data-private>
                ({t`Unenrolled: `}
                {unsubscribedEnrollment?.learner?.name ?? `------`})
              </Box>
            </Box>
          </li>
        ))}
      </ul>
    </Box>
  );
}

function ResumeYourSubscriptionsPriceBreakdown({
  totalCents,
  enrollments
}: {
  totalCents: number;
  enrollments: PaymentFailureEnrollment[];
}) {
  const { t } = useTranslation(
    "client\\components\\account\\UpdatePaymentMethodDueToPaymentFailure"
  );
  const periodText = t` charged weekly`;
  // TODO: Account for localizing currency?
  // const { renderCurrencyString, isBaseCurrency, currencyDisclaimer } = useLocalPriceDisplay();
  const total = `$${centsToDollars(totalCents)} USD`;

  // TODO: Account for promos / coupons

  return (
    <Box
      sx={{
        backgroundColor: "#F0F3FF",
        borderRadius: pxToRem(8),
        padding: pxToRem(16)
      }}
    >
      {enrollments.length > 0 &&
        enrollments.map(enrollment => (
          <Box
            flex
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: "0.25em"
            }}
          >
            <Box data-private>
              {enrollment?.learner?.name ?? `-----`} ({t`age `}
              {enrollment?.learner?.age ?? `--`})
            </Box>
            <PriceDisplay
              amountCents={enrollment?.priceCents ?? 0}
              periodText={periodText}
            />
          </Box>
        ))}
      <hr
        color="primary.200"
        style={{ marginTop: "12px", marginBottom: "12px" }}
      />
      <Box
        flex
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "0.25em"
        }}
      >
        <Typography emphasized>{t`Total due: `}</Typography>
        <Typography emphasized>{total}</Typography>
      </Box>
    </Box>
  );
}

type ResumeYourSubscriptionsContentProps = {
  onClose: () => void;
  onGoBack?: () => void;
  muteAlert?: () => void;
};

function ResumeYourSubscriptionsContent({
  onClose,
  onGoBack = () => {},
  muteAlert = () => {}
}: ResumeYourSubscriptionsContentProps) {
  const { t } = useTranslation(
    "client\\components\\account\\UpdatePaymentMethodDueToPaymentFailure"
  );
  const [isAuthorizationChecked, setIsAuthorizationChecked] =
    React.useState(false);
  const [isResumeSuccessful, setisResumeSuccessful] = React.useState(false);

  const { data, loading } = useEnrollmentsPausedDueToPaymentFailure();

  const enrollmentsPausedDueToPaymentFailure =
    data?.currentUser?.activeOngoingEnrollmentsPausedDueToPaymentFailure ?? [];

  // TODO: Figure out which enrollments were ended due to the classes being full, and populate this const
  const unsubscribedEnrollments: PaymentFailureEnrollment[] = [];

  const costOfResumingAllEnrollments =
    enrollmentsPausedDueToPaymentFailure?.reduce((curr, enrollment) => {
      return curr + (enrollment?.priceCents ?? 0);
    }, 0) ?? 0;

  const [pauseOngoingEnrollments, { loading: isResumeInProgress }] =
    useMutation<
      PauseOngoingEnrollmentsMutation,
      PauseOngoingEnrollmentsMutationVariables
    >(pauseOngoingEnrollmentsMutation);

  const handlePayNowAndResume = async () => {
    // Create PauseOngoingEnrollmentInput objects out of all the enrollments for the mutation
    const pauseOngoingEnrollmentsInput =
      enrollmentsPausedDueToPaymentFailure?.map(enrollment => {
        return {
          enrollmentUid: enrollment?.uid!,
          // pass in an empty DateArray to cancel the pause AKA resume subscription
          dates: []
        };
      });

    // Call mutation to update pause windows and resume all enrollments
    const { data } = await pauseOngoingEnrollments({
      variables: {
        input: pauseOngoingEnrollmentsInput
      }
    });
    // If we got back a response, the mutation was successful
    if (data) {
      setisResumeSuccessful(true);
      muteAlert();
      onClose();
    } else {
      // If not, there's an error
      // TODO: Handle errors? Figure out which enrollments were stopped due to class being full?
    }
  };

  return (
    <Box flex sx={{ flexDirection: "column", gap: "16px" }}>
      <Typography
        variant="h4"
        sx={{
          paddingBottom: "0.5em",
          borderBottom: "1px solid",
          borderColor: "grey.100"
        }}
      >
        {t`Resume Your Subscriptions`}
      </Typography>

      {loading ? (
        <LoadingWithMessage>{t`Please wait while we load your subscriptions.`}</LoadingWithMessage>
      ) : isResumeInProgress ? (
        <LoadingWithMessage>{t`Please wait while we process your enrollment.`}</LoadingWithMessage>
      ) : (
        <Box>
          <Box flex sx={{ flexDirection: "column", gap: pxToRem(21) }}>
            {unsubscribedEnrollments.length > 0 && (
              <UnsubscribedClassesList
                unsubscribedEnrollments={unsubscribedEnrollments}
              />
            )}

            <ResumeYourSubscriptionsPriceBreakdown
              enrollments={enrollmentsPausedDueToPaymentFailure}
              totalCents={costOfResumingAllEnrollments}
            />

            <ResumeSubscriptionsAuthorization
              setIsChecked={setIsAuthorizationChecked}
              isChecked={isAuthorizationChecked}
              isPaymentProcessing={isResumeInProgress}
              isPaymentSuccessful={isResumeSuccessful}
              totalCents={costOfResumingAllEnrollments}
            />
          </Box>
          <Box
            flex
            sx={{
              justifyContent: "space-between"
            }}
          >
            <Button
              variant="outlined"
              disabled={!isAuthorizationChecked || isResumeInProgress}
              // No action needed here since we'll retry payment at end of pause period via extension script
              onClick={() => {
                muteAlert();
                onClose();
              }}
            >
              {/* TODO: Add date for end of pause period, and remove button in pre-paused state (use flag, make other button full width) */}
              {t`Charge Me on Sunday`}
            </Button>
            <Button
              variant="contained"
              disabled={!isAuthorizationChecked || isResumeInProgress}
              onClick={handlePayNowAndResume}
            >
              {t`Pay Now and Resume`}
            </Button>
          </Box>

          <Button
            variant="link"
            onClick={onGoBack}
            disabled={isResumeInProgress}
            sx={{
              marginTop: "16px",
              width: "100%"
            }}
          >
            {t("Go Back")}
          </Button>
        </Box>
      )}
    </Box>
  );
}

type PaymentMethodFormOptions = {
  title?: string;
  subtitle?: React.ReactNode;
  description?: string;
  alertMessage?: string;
  buttonLabel?: string;
  savePaymentMethodsLoadingText?: string;
};
type PaymentMethodModalProps = {
  open: boolean;
};
type PaymentMethodModalContentProps = {
  onClose?: () => void;
  onPaymentSaved?: () => Promise<void>;
  onGoBack?: () => void;
  muteAlert?: () => void;
  formOptions?: PaymentMethodFormOptions;
};

/**
 * This is a consumer for <StripeProvider /> thus the nested components
 */
function SavePaymentMethodModalContent({
  onClose = () => {},
  onPaymentSaved = async () => {},
  onGoBack,
  muteAlert = () => {},
  formOptions = {}
}: PaymentMethodModalContentProps) {
  const { t } = useTranslation(
    "client\\components\\account\\UpdatePaymentMethodDueToPaymentFailure"
  );
  const getCard = useGetCard();
  const [zipcode, setZipcode] = React.useState<string | null>(null);
  const [isComplete, setIsComplete] = React.useState(false);
  const [paymentError, setPaymentError] = React.useState<string | null>(null);
  const errorMessage = useDefaultStripeErrorMessage();
  const [resumeYourSubscriptions, setResumeYourSubscriptions] =
    React.useState(false);

  const {
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    paymentMethods
  } = usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;

  let {
    title = null,
    subtitle = defaultPaymentMethod
      ? t`This is your default card.`
      : t`This will be your default card.`,
    description = defaultPaymentMethod
      ? t`We use this card to pay for renewing all of your classes with recurring weekly payments.`
      : t`We will use this card to pay for renewing all of your classes with recurring weekly payments.`,
    alertMessage = null,
    buttonLabel = t`Save Card`,
    savePaymentMethodsLoadingText = t`Please wait while we save your credit card`
  } = formOptions;

  const [createSetupIntent, { error, loading }] = useCreateSetupIntent();

  const handleSaveCard = React.useCallback(async () => {
    setPaymentError(null);
    const card = getCard();
    // @ts-ignore TS(2322): Type 'StripeCardNumberElement | null' is not assig... Remove this comment to see the full error message
    await createSetupIntent({ card, zipcode });
    await onPaymentSaved();
    setResumeYourSubscriptions(true);
  }, [getCard, createSetupIntent, zipcode, onPaymentSaved]);

  React.useEffect(() => {
    setPaymentError(null);
  }, [isComplete, zipcode]);

  if (resumeYourSubscriptions) {
    return (
      <ResumeYourSubscriptionsContent
        onClose={onClose}
        onGoBack={() => setResumeYourSubscriptions(false)}
        muteAlert={muteAlert}
      />
    );
  } else {
    return (
      <Box>
        <Typography
          variant="h4"
          sx={{
            paddingBottom: "8px",
            borderBottom: "1px solid",
            borderColor: "grey.100"
          }}
        >
          {title
            ? title
            : defaultPaymentMethod
            ? t`Update Your Payment Method`
            : t`Replace With New Card`}
        </Typography>

        {subtitle && (
          <Box
            sx={{
              marginTop: "1em",
              marginBottom: "1em"
            }}
          >
            <Typography variant="h6" sx={{ paddingBottom: "13px" }}>
              {subtitle}
            </Typography>
            {description}
          </Box>
        )}
        {alertMessage && defaultPaymentMethod && (
          <Alert severity="error" sx={{ marginBottom: "16px" }}>
            {alertMessage}
          </Alert>
        )}
        <Box
          sx={{
            paddingTop: "0.5em",
            paddingBottom: "0.5em"
          }}
        >
          {paymentMethodsError ? (
            <ErrorMessage value={errorMessage} />
          ) : paymentMethodsLoading ? (
            <LoadingWithMessage>
              {t`Please wait while we load your payment methods`}
            </LoadingWithMessage>
          ) : defaultPaymentMethod ? (
            <StripeSavedCards
              defaultPaymentMethod={defaultPaymentMethod}
              disabled={paymentMethodsLoading}
              resumeYourSubscriptionsCallback={() =>
                setResumeYourSubscriptions(true)
              }
              updatePaymentMethodDueToPaymentFailure={true}
            />
          ) : (
            <>
              <StripeNewCard
                setIsCardComplete={setIsComplete}
                setBillingDetails={(billingDetails: BillingDetails) =>
                  // @ts-ignore TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                  setZipcode(billingDetails?.address?.postal_code)
                }
              />
              {loading ? (
                <LoadingWithMessage>
                  {savePaymentMethodsLoadingText}
                </LoadingWithMessage>
              ) : (
                <>
                  <ErrorMessage
                    value={paymentError || (error && error.message)}
                  />
                  <Button
                    variant="contained"
                    disabled={!isComplete || loading}
                    onClick={handleSaveCard}
                    sx={{
                      width: "100%"
                    }}
                  >
                    {buttonLabel}
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
        {onGoBack && (
          <Button
            variant="link"
            onClick={onGoBack}
            sx={{
              width: "100%"
            }}
            disabled={loading}
          >
            {t("Go Back")}
          </Button>
        )}
      </Box>
    );
  }
}

function UpdatePaymentMethodDueToPaymentFailure(
  props: PaymentMethodModalContentProps
) {
  return (
    <StripeProvider>
      <SavePaymentMethodModalContent {...props} />
    </StripeProvider>
  );
}

export function UpdatePaymentMethodDueToPaymentFailureModal({
  open,
  onClose,
  ...otherProps
}: PaymentMethodModalProps & PaymentMethodModalContentProps) {
  return (
    <Modal open={open} onClose={onClose}>
      <UpdatePaymentMethodDueToPaymentFailure
        onClose={onClose}
        {...otherProps}
      />
    </Modal>
  );
}
